import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Share2, MessageCircle, ImageDown } from "lucide-react";
import { motion } from "framer-motion";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { renderBookingCard, shareBookingImage } from "@/lib/bookingCard";
import { useLanguage } from "@/contexts/LanguageContext";

export default function ShareBooking({ booking, clubName, kind = "court" }) {
  const { t, dateFnsLocale } = useLanguage();
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !booking) return;
    renderBookingCard(booking, { clubName: clubName || "D&D Padel", kind }).then(c => setPreview(c.toDataURL("image/png"))).catch(() => {});
  }, [open, booking, clubName, kind]);

  if (!booking || !booking._id) return null;

  const club = clubName || "D&D Padel";
  const niceDate = format(parseISO(booking.date), "do MMMM yyyy", { locale: dateFnsLocale });
  const shareText = t("share.template", { club, date: niceDate, start: booking.start_time, end: booking.end_time });
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  const canShareFiles = typeof navigator !== "undefined" && !!navigator.canShare;

  const handleImage = async () => {
    setBusy(true);
    try {
      const r = await shareBookingImage(booking, { clubName: club, kind });
      if (r === "downloaded") toast.success(t("share.saved"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="share-booking-btn" variant="outline" size="sm" className="rounded-full gap-1.5 border-teal/30 text-teal hover:bg-teal/10">
          <Share2 size={14} /> {t("share.btn")}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Share2 size={18} className="text-teal" /> {t("share.title")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-2xl overflow-hidden border border-border/40 bg-muted/20 aspect-square" data-testid="share-preview">
            {preview ? <img src={preview} alt="Booking card" className="w-full h-full object-cover" data-testid="share-preview-img" /> : <div className="w-full h-full flex items-center justify-center"><div className="animate-spin h-6 w-6 border-2 border-coral rounded-full border-t-transparent" /></div>}
          </div>

          <button onClick={handleImage} disabled={busy || !preview} data-testid="share-image-btn"
            className="flex items-center gap-3 p-3.5 rounded-xl bg-[#25D366]/10 border border-[#25D366]/20 hover:bg-[#25D366]/15 transition-colors w-full text-left disabled:opacity-50">
            <div className="w-10 h-10 rounded-xl bg-[#25D366] flex items-center justify-center shrink-0">{canShareFiles ? <MessageCircle size={20} className="text-white" /> : <ImageDown size={20} className="text-white" />}</div>
            <div className="flex-1">
              <p className="font-semibold text-sm">{canShareFiles ? t("share.image") : t("share.save")}</p>
              <p className="text-[10px] text-muted-foreground">{canShareFiles ? t("share.image_sub") : t("share.save_sub")}</p>
            </div>
          </button>

          <motion.a href={whatsappUrl} target="_blank" rel="noopener noreferrer" whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }} data-testid="share-whatsapp"
            className="flex items-center gap-3 p-3 rounded-xl bg-muted/30 border border-border/30 hover:bg-muted/50 transition-colors w-full">
            <div className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center shrink-0"><MessageCircle size={16} className="text-muted-foreground" /></div>
            <div className="flex-1">
              <p className="font-semibold text-xs">{t("share.text")}</p>
            </div>
          </motion.a>
        </div>
      </DialogContent>
    </Dialog>
  );
}
